import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/database.types';

type Profile = Database['public']['Tables']['profiles']['Row'] & {
  level_progress_percentage?: number;
  cerebrix_needed_for_next?: number;
  lesson_cerebrix?: number;
  game_cerebrix?: number;
  current_badge?: string;
  current_badge_icon?: string;
};

export interface UseProfileResult {
  userProfile: Profile | null;
  isLoading: boolean;
  error: Error | null;
  loadProfile: () => Promise<void>;
}

export function useProfile(): UseProfileResult {
  const [userProfile, setUserProfile] = useState<Profile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadProfile = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        console.log('No session found');
        setIsLoading(false);
        return;
      }

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select(`
          id,
          email,
          role,
          student_name,
          tutor_name,
          whatsapp,
          country_code,
          avatar_url,
          created_at,
          updated_at,
          subscription_status,
          subscription_start_date,
          subscription_end_date,
          trial_start_date,
          trial_expired,
          trial_used,
          level,
          total_points,
          next_level_points,
          cerebrix,
          lesson_cerebrix,
          game_cerebrix,
          is_partner
        `)
        .eq('id', session.user.id)
        .single();

      if (profileError) {
        if (profileError.code === 'PGRST116') {
          const { data: userData } = await supabase.auth.getUser();
          
          const newProfile = {
            id: session.user.id,
            email: session.user.email,
            role: 'user',
            tutor_name: userData.user?.user_metadata?.tutor_name || '',
            student_name: userData.user?.user_metadata?.student_name || '',
            whatsapp: userData.user?.user_metadata?.whatsapp || '',
            country_code: userData.user?.user_metadata?.country_code || 'PE',
            cerebrix: 0,
            lesson_cerebrix: 0,
            game_cerebrix: 0,
            cerebrix_level: 1,
            level_progress_percentage: 0,
            cerebrix_needed_for_next: 100,
            current_badge: 'Aprendiz Financiero',
            current_badge_icon: '🔰',
            trial_used: false,
            trial_expired: false,
            trial_start_date: null,
            subscription_status: 'trial',
            subscription_end_date: null,
            is_partner: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };

          const { error: insertError } = await supabase
            .from('profiles')
            .insert([newProfile]);

          if (insertError) throw insertError;
          setUserProfile(newProfile);
        } else {
          throw profileError;
        }
      } else {
        setUserProfile(profile);
      }
    } catch (e) {
      console.error('Error loading profile:', e);
      setError(e as Error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    const profileSubscription = supabase
      .channel('profile-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'profiles'
        },
        async (payload) => {
          console.log('Profile change detected:', payload);
          if (payload.new && typeof payload.new === 'object') {
            setUserProfile(payload.new as Profile);
          }
        }
      )
      .subscribe();

    return () => {
      profileSubscription.unsubscribe();
    };
  }, []);

  return { userProfile, isLoading, error, loadProfile };
}
